import {
  useZapTemplate,
  SignUpForm,
  useInvitation,
  CAMPAIGN_PROJECT_HASHBROWN,
  Invitation,
} from '@zapier/authentication';
import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { resolveSignUpNextUrl } from '@zapier/identity-utils';

import { AuthLayout, InvitationError } from '../../global';
import { useNotification } from '../../global/Notifications';
import { TEAM_INVITATION_STATUS_MISMATCHED_EMAIL } from '../../../constants';

import { getZapTemplateId } from './utils';
import { SignUpPromo } from './SignUpPromo';
import { ZapTemplatePromo } from './ZapTemplatePromo';
import { ZapTemplateTitle } from './ZapTemplateTitle';
import { HashbrownTrialPromo } from './HashbrownTrialPromo';
import { InvitationPromo } from './InvitationPromo';

export type SignupPageProps = {
  invitationData?: {
    isValid: boolean;
    invitation?: Invitation;
    error?: string;
    code?: string;
  };
};

export function SignupPage({ invitationData }: SignupPageProps) {
  const invitation = invitationData?.invitation;
  const code = invitationData?.code;
  const router = useRouter();

  const next = resolveSignUpNextUrl(router.query.next as string | undefined);
  const ztId = getZapTemplateId(router);
  const zapTemplate = useZapTemplate(ztId);
  const { createNotification } = useNotification();
  const { declineInvitation } = useInvitation(code, {
    skip: true,
  });

  useEffect(() => {
    if (
      router.isReady &&
      router.query.notification_code === TEAM_INVITATION_STATUS_MISMATCHED_EMAIL
    ) {
      createNotification({
        title: "Email doesn't match invite",
        message:
          "The email you've used to sign up doesn't match the email that was invited to the account. Please try again.",
        type: 'error',
      });
    }
  }, [router.isReady, createNotification, router.query.notification_code]);

  if (invitationData && (invitationData?.error || !invitationData?.isValid)) {
    return (
      <InvitationError error={invitationData?.error} url={router.asPath} />
    );
  }

  const title = zapTemplate?.zapTemplate ? (
    <ZapTemplateTitle zapTemplate={zapTemplate.zapTemplate} />
  ) : null;

  const campaign = router.query.campaign;

  async function handleDeclineButtonClick() {
    if (code) {
      await declineInvitation(code);
      router.push(`/app/invitation/${code}/decline`);
    }
  }

  function getPromo() {
    // Now that the promo is based on url params, the router must be ready
    // before we can display anything
    // Optional, display loading for SSR? I don't think users will
    // notice anything here
    if (!router.isReady) {
      return null;
    }
    if (zapTemplate.zapTemplate) {
      return <ZapTemplatePromo zapTemplate={zapTemplate.zapTemplate} />;
    } else if (campaign === CAMPAIGN_PROJECT_HASHBROWN) {
      return <HashbrownTrialPromo />;
    } else if (invitation) {
      return (
        <InvitationPromo
          invitation={invitation}
          handleDeclineButtonClick={handleDeclineButtonClick}
        />
      );
    } else {
      return <SignUpPromo />;
    }
  }
  const promo = getPromo();

  return (
    <AuthLayout promo={promo} showPromo={true} title={title}>
      <SignUpForm
        enableEmailAutoFocus={true}
        googleCaptchaSiteKey={
          process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_SITE_KEY || ''
        }
        googleClientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
        invitation={invitationData?.isValid ? invitationData?.invitation : null}
        next={next}
        routeTo={router.push}
        campaign={campaign}
        useCase={
          (router.query.use_case || router.query.use_case_slug) as string
        }
      />
    </AuthLayout>
  );
}
